import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatTerminDate } from "@/shared/format/termin-date";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { stripHtml } from "@/shared/lib/html";
import { Link } from "@tanstack/react-router";
import { Calendar, MapPin, Users } from "lucide-react";
import { usePublicActions } from "./public-actions";

/** Veřejný seznam všech otevřených termínů. */
export function PublicTerminyPage() {
  const { openDiagnoza } = usePublicActions();
  const query = trpc.hca.terminy.listPublic.useQuery({});
  const listRef = useGsapReveal<HTMLDivElement>({
    childrenSelector: "[data-termin-card]",
    stagger: 0.1,
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-16" data-testid="hca-public-terminy-page">
      <header className="mb-10 max-w-3xl">
        <div className="text-sm font-semibold uppercase tracking-wider text-primary">
          Semináře HCA Moravia
        </div>
        <h1 className="mt-2 text-4xl font-bold tracking-tight">Nadcházející termíny</h1>
        <p className="mt-3 text-muted-foreground">
          Přehled otevřených termínů pro majitele, jednatele, ředitele a manažerské týmy. U každého
          termínu se&nbsp;můžete rovnou přihlásit.
        </p>
      </header>

      {query.isError ? (
        <div
          className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive"
          data-testid="hca-public-terminy-error"
          aria-live="polite"
        >
          Načtení termínů selhalo: {query.error.message}
        </div>
      ) : query.isPending ? (
        <div className="space-y-3" data-testid="hca-public-terminy-skeleton">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : query.data.length === 0 ? (
        <div
          className="rounded-lg border border-border bg-background p-10 text-center"
          data-testid="hca-public-terminy-empty"
        >
          <p className="text-muted-foreground">
            Momentálně není otevřený žádný termín. Spusťte diagnostiku a doporučíme vám vhodný další
            krok.
          </p>
          <Button
            type="button"
            className="mt-4"
            onClick={openDiagnoza}
            data-testid="hca-public-terminy-empty-diagnoza-button"
          >
            Spustit diagnostiku
          </Button>
        </div>
      ) : (
        <div ref={listRef} className="space-y-3" data-testid="hca-public-terminy-list">
          {query.data.map((termin) => (
            <TerminListRow key={termin.id} termin={termin} />
          ))}
        </div>
      )}
    </div>
  );
}

interface TerminListRowProps {
  termin: {
    id: string;
    datumOd: string;
    datumDo: string;
    misto: string;
    kapacitaMax: number;
    kapacitaObsazena: number;
    product?: {
      nazev?: string | null;
      slug?: string | null;
      kategorie?: string | null;
      popis?: string | null;
    } | null;
  };
}

function TerminListRow({ termin }: TerminListRowProps) {
  const volno = Math.max(0, termin.kapacitaMax - termin.kapacitaObsazena);
  const nazev = termin.product?.nazev ?? "Seminář HCA";
  const kategorie = termin.product?.kategorie ?? "Seminář";

  return (
    <article
      data-termin-card
      className="rounded-lg border border-border bg-background p-5 transition hover:border-primary/40 hover:shadow-sm"
      data-testid={`hca-public-terminy-row-${termin.id}`}
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            {kategorie}
          </div>
          <h2 className="mt-1 text-xl font-semibold text-foreground">{nazev}</h2>
          <p className="mt-2 line-clamp-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            {stripHtml(termin.product?.popis ?? "") ||
              "Praktický program pro vedení firmy a manažerský tým. Přihláška je nezávazná — po odeslání se vám ozveme a potvrdíme účast."}
          </p>
          {termin.product?.slug ? (
            <Link
              to="/seminare/$slug"
              params={{ slug: termin.product.slug }}
              className="mt-1 inline-block text-primary text-sm font-medium hover:underline"
              data-testid={`hca-public-terminy-row-more-${termin.id}`}
            >
              Více o semináři →
            </Link>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" aria-hidden="true" />
              {formatTerminDate(termin.datumOd, termin.datumDo)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              {termin.misto}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-4 w-4" aria-hidden="true" />
              Kapacita:{" "}
              <strong className="text-foreground">
                {volno} z {termin.kapacitaMax} volných
              </strong>
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <Button asChild data-testid={`hca-public-terminy-row-register-${termin.id}`}>
            <Link to="/registrace/$terminId" params={{ terminId: termin.id }}>
              Přihlásit se
            </Link>
          </Button>
          {termin.product?.slug ? (
            <Button
              variant="outline"
              asChild
              data-testid={`hca-public-terminy-row-detail-${termin.id}`}
            >
              <Link to="/seminare/$slug" params={{ slug: termin.product.slug }}>
                Detail
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
